import { Assert } from "../../Assert";
import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ApplicationInformationService } from "../../RadWindow";
import { ServiceManager } from "../../ServiceManager";
import { JsxFragment } from "../JsxFragment";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { NAMESPACE } from "./_Namespace";

export type ImagePropertyValues =
    ControlPropertyValues
    & {
        alt: string;
        resizeToFit: boolean;
        src: string;
    };

export type ImageProperties = {
    [name in keyof ImagePropertyValues]: Property<ImagePropertyValues[name]>
};

export interface IImage extends IControl, ImageProperties
{
}

export class Image extends Control implements IImage
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Image";
    public static override className = "Image";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ImagePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                resizeToFit: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public alt!: PropertyString;
    public resizeToFit!: PropertyBoolean;
    public src!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ImagePropertyValues>)
    {
        super(propertyValues);

        // Trigger Property Changes
        this.alt.triggerChange();
        this.resizeToFit.triggerChange();
        this.src.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public setSrcAsync(value: string)
    {
        const
            $$element = this.$$element.get(),
            src = this._fixSrc(value);

        return new Promise((resolve, reject) =>
        {
            Assert.isNotNullOrUndefined($$element, "$$element");

            if (($$element[0] as HTMLImageElement).src === src)
                resolve(undefined);
            else
            {
                $$element[0].onload = () =>
                {
                    if (this.resizeToFit.get())
                        this._resizeToFit();

                    resolve(undefined);
                };

                $$element[0].onerror = error =>
                {
                    reject(error);
                };

                this.src.set(src);
            }
        });
    }

    // #endregion

    // #region Protected Methods

    protected _altChanged(change: PropertyChange<string>)
    {
        this.$$element.get()?.attr("alt", change.currentValue ?? StringHelper.empty);
    }
    protected _fixSrc(src: string)
    {
        return (
            src?.startsWith("/")
                ? ServiceManager.getRequired(ApplicationInformationService).virtualDirectory + src.substring(1)
                : src
        );
    }
    protected _resizeToFit()
    {
        const
            $$element = this.$$element.get() as JNative<HTMLImageElement>,
            $$parentElement = $$element?.parent(),
            parentElementHeight = $$parentElement?.outerHeight(),
            parentElementWidth = $$parentElement?.outerWidth();

        let
            ratio;

        if (parentElementHeight && parentElementWidth)
        {
            const
                elementAspectRatio = parentElementWidth / parentElementHeight,
                imageAspectRatio = $$element[0].naturalWidth / $$element[0].naturalHeight;

            if (imageAspectRatio === elementAspectRatio)
            {
                $$element.css({
                    height: StringHelper.empty,
                    left: StringHelper.empty,
                    top: StringHelper.empty,
                    width: StringHelper.empty
                });
            }
            else if (imageAspectRatio > elementAspectRatio)
            {
                ratio = parentElementHeight * imageAspectRatio / parentElementWidth;

                $$element.css({
                    height: StringHelper.empty,
                    left: StringHelper.format("{0}%", (1 - ratio) / 2 * 100),
                    top: StringHelper.empty,
                    width: StringHelper.format("{0}%", ratio * 100)
                });
            }
            else
            {
                ratio = parentElementWidth / imageAspectRatio / parentElementHeight;

                $$element.css({
                    height: StringHelper.format("{0}%", ratio * 100),
                    left: StringHelper.empty,
                    top: StringHelper.format("{0}%", (1 - ratio) / 2 * 100),
                    width: StringHelper.empty
                });
            }
        }
    }
    protected _resizeToFitChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            $$(window).on("resize", { self: this }, this.onWindowResize);
        else
            $$(window).off("resize", { self: this }, this.onWindowResize);
    }
    protected _srcChanged(change: PropertyChange<string>)
    {
        this.$$element.get()?.attr("src", change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ImagePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.alt = new PropertyString(this, propertyValues.alt, this._altChanged);
        this.resizeToFit = new PropertyBoolean(this, propertyValues.resizeToFit, this._resizeToFitChanged);
        this.src = new PropertyString(this, propertyValues.src, this._srcChanged);

        this.src.overrideSet((value, next) =>
        {
            const
                $$element = this.$$element.get(),
                src = value ? this._fixSrc(value) : null;

            Assert.isNotNullOrUndefined($$element, "$$element");

            $$element[0].onload = () =>
            {
                if (this.resizeToFit.get())
                    this._resizeToFit();
            };

            next(src);
        })
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <img>
                {children}
            </img>
        );
    }

    // #endregion

    // #region Event Handlers

    protected onWindowResize(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._resizeToFit();
    }

    // #endregion

    // #endregion
}